import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    Stack,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    InputLabel,
} from "@mui/material";
import DatePickerComponent from "../../Common/DatePicker";
import Select from "../../Common/Select";
import MultipleSelectJson from "../../Common/MultipleSelectJson";
import { Clear as ClearIcon } from "@mui/icons-material";

import { useGenerateDashboardReportQuery } from "../../../store/reports/reportsApis";
import { useGetRegionsDropdownQuery } from "../../../store/masters/mastersApis";

const DashboardReport = ({ reportType, filters, onFilterUpdate, onFilterReset, onExport, onRefresh }) => {
    const [localFilters, setLocalFilters] = useState(filters);

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    // Fetch dropdown data
    const { data: regionsData } = useGetRegionsDropdownQuery();

    const { data: reportData, isLoading, error, refetch } = useGenerateDashboardReportQuery(localFilters);

    // Convert IDs to objects for MultipleSelectJson display
    const convertIdsToObjects = (ids, options) => {
        if (!Array.isArray(ids) || ids.length === 0) return [];
        return ids.map((id) => options.find((option) => option.value === id)).filter(Boolean);
    };

    const handleFilterChange = (field, value) => {
        // Extract values from MultipleSelectJson objects
        let processedValue = value;
        if (field.includes("_ids") && Array.isArray(value)) {
            processedValue = value.map((item) => item.value || item);
            // Remove duplicates
            processedValue = [...new Set(processedValue)];
        }

        const newFilters = { ...localFilters, [field]: processedValue };
        setLocalFilters(newFilters);
        onFilterUpdate(newFilters);
    };

    const handleReset = () => {
        setLocalFilters({
            start_date: null,
            end_date: null,
            region_ids: [],
            format: "json",
        });
        onFilterReset();
    };

    return (
        <Box>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Filters
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePickerComponent
                            label="Start Date"
                            value={localFilters.start_date}
                            onChange={(date) => handleFilterChange("start_date", date)}
                            name="start_date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePickerComponent
                            label="End Date"
                            value={localFilters.end_date}
                            onChange={(date) => handleFilterChange("end_date", date)}
                            name="end_date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <InputLabel sx={{ fontSize: "14px", color: "#454545", fontWeight: "500" }} shrink={false}>
                                Regions
                            </InputLabel>
                            <MultipleSelectJson
                                id="region-select"
                                label="Regions"
                                value={convertIdsToObjects(localFilters.region_ids || [], regionsData || [])}
                                onChange={(value) => handleFilterChange("region_ids", value)}
                                name="region_ids"
                                options={regionsData || []}
                                size="small"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Select
                            label="Format"
                            value={localFilters.format || "json"}
                            onChange={(e) => handleFilterChange("format", e.target.value)}
                            name="format"
                            options={[
                                { value: "json", label: "JSON" },
                                { value: "csv", label: "CSV" },
                                { value: "excel", label: "Excel" },
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleReset} size="small">
                                Reset
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {isLoading && (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error?.data?.message || "Failed to generate dashboard report"}
                </Alert>
            )}

            {reportData && !isLoading && (
                <Box>
                    {/* Overview Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Customers
                                    </Typography>
                                    <Typography variant="h4">{reportData.overview?.total_customers || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Users
                                    </Typography>
                                    <Typography variant="h4">{reportData.overview?.total_users || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Orders
                                    </Typography>
                                    <Typography variant="h4">{reportData.overview?.total_orders || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Revenue
                                    </Typography>
                                    <Typography variant="h4">
                                        ₹{reportData.overview?.total_revenue?.toLocaleString() || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Warranty Registrations
                                    </Typography>
                                    <Typography variant="h4">
                                        {reportData.overview?.total_warranty_registrations || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Insurance Renewals
                                    </Typography>
                                    <Typography variant="h4">
                                        {reportData.overview?.total_insurance_renewals || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Paid Orders
                                    </Typography>
                                    <Typography variant="h4">{reportData.overview?.paid_orders || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Pending Orders
                                    </Typography>
                                    <Typography variant="h4">{reportData.overview?.pending_orders || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Top Products */}
                    {reportData.top_products && reportData.top_products.length > 0 && (
                        <Paper sx={{ p: 2, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Top Products
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Product</TableCell>
                                            <TableCell>Order Count</TableCell>
                                            <TableCell>Total Revenue</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.top_products.map((product, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{product.product?.name || "Unknown Product"}</TableCell>
                                                <TableCell>{product.order_count}</TableCell>
                                                <TableCell>₹{product.total_revenue?.toLocaleString() || 0}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}

                    {/* Regional Performance */}
                    {reportData.regional_performance && reportData.regional_performance.length > 0 && (
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Regional Performance
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Region</TableCell>
                                            <TableCell>Order Count</TableCell>
                                            <TableCell>Total Revenue</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.regional_performance.map((region, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{region.region_name || "Unknown Region"}</TableCell>
                                                <TableCell>{region.order_count}</TableCell>
                                                <TableCell>₹{region.total_revenue?.toLocaleString() || 0}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    )}
                </Box>
            )}

            {!reportData && !isLoading && !error && (
                <Alert severity="info">No data found for the selected filters.</Alert>
            )}
        </Box>
    );
};

export default DashboardReport;
